import { useState } from "react";
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { renameDossier, deleteDossier } from "@/lib/questionnaire";

export default function DossierActions({ dossier: d, onRenamed, onDeleted }) {
    const [renaming, setRenaming] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [nom, setNom] = useState(d.nom_mission || "");
    const [busy, setBusy] = useState(false);
    const stop = (e) => e.stopPropagation();

    async function rename(e) {
        e.preventDefault();
        if (!nom.trim()) return toast.error("Le nom de la mission est obligatoire");
        setBusy(true);
        try {
            const r = await renameDossier(d.id, nom.trim());
            onRenamed?.(d.id, r.nom_mission);
            setRenaming(false);
            toast.success("Mission renommée");
        } catch (err) {
            toast.error(err.message);
        } finally {
            setBusy(false);
        }
    }

    async function remove() {
        setBusy(true);
        try {
            const r = await deleteDossier(d.id);
            onDeleted?.(d.id);
            toast.success("Dossier supprimé", { description: `${r.fichiers_supprimes} fichier(s) et toutes les données associées supprimés.` });
        } catch (err) {
            toast.error("Suppression impossible", { description: err.message });
        } finally {
            setBusy(false);
            setDeleting(false);
        }
    }

    return (
        <div onClick={stop} onKeyDown={stop} className="flex items-center gap-1">
            <Button variant="ghost" size="icon" data-testid={`dossier-rename-icon-${d.id}`} onClick={() => { setNom(d.nom_mission || ""); setRenaming(true); }} className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground" aria-label="Renommer la mission" title="Renommer la mission">
                <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
            </Button>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" data-testid={`dossier-menu-${d.id}`} className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground" aria-label="Actions">
                        <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem data-testid={`dossier-rename-${d.id}`} onSelect={() => { setNom(d.nom_mission || ""); setRenaming(true); }}>
                        <Pencil className="h-4 w-4" strokeWidth={1.75} /> Éditer le nom de la mission
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid={`dossier-delete-${d.id}`} onSelect={() => setDeleting(true)} className="text-destructive focus:text-destructive">
                        <Trash2 className="h-4 w-4" strokeWidth={1.75} /> Supprimer le dossier
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <Dialog open={renaming} onOpenChange={setRenaming}>
                <DialogContent data-testid="rename-dialog" onClick={stop}>
                    <form onSubmit={rename} className="space-y-5">
                        <DialogHeader>
                            <DialogTitle className="font-display">Nom de la mission</DialogTitle>
                            <DialogDescription>{d.entreprises?.nom_entreprise} · SIREN {d.entreprises?.siren}</DialogDescription>
                        </DialogHeader>
                        <Input data-testid="rename-input" value={nom} onChange={(e) => setNom(e.target.value)} maxLength={160} autoFocus className="h-11 bg-card" />
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={() => setRenaming(false)} className="rounded-full">Annuler</Button>
                            <Button type="submit" data-testid="rename-submit" disabled={busy || !nom.trim()} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">Enregistrer</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <AlertDialog open={deleting} onOpenChange={setDeleting}>
                <AlertDialogContent data-testid="delete-dialog" onClick={stop}>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display">Supprimer définitivement ce dossier ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            « {d.nom_mission || d.entreprises?.nom_entreprise} » sera supprimé avec tous ses fichiers de dataroom, KPI, questionnaire et synthèses IA. Cette action est irréversible. L&apos;entreprise et le client sont conservés.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-cancel" className="rounded-full">Annuler</AlertDialogCancel>
                        <AlertDialogAction data-testid="delete-confirm" onClick={(e) => { e.preventDefault(); remove(); }} disabled={busy} className="rounded-full bg-destructive hover:bg-destructive/90 text-white">
                            {busy ? "Suppression…" : "Supprimer"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
